import React, { useMemo, useState } from "react";
import { useGlobalData } from "../hooks/useGlobalHooks";
import CancelConfirmationModal from "./CancelConfirmationModal";
import ExitSuccessModal from "./ExitSuccessModal";
import apiClient from "../utils/apiClient";
import server from "../utils/serverConfig";
import BulkModifyModal from "./BulkModifyModal";
import { Pencil } from "lucide-react";
import { toast } from "react-hot-toast";
import { OptimizedNumberCell, LiveLTPCell } from "./OptimizedCell";

/**
 * Pledge Plus Order Comparison Modal
 * Adapted from OrderComparisonModal for Pledge Plus (Assets instead of Indices)
 */
export function PledgePlusOrderComparisonModal({
    isOpen,
    onClose,
    clientData,
    orders,
    positions, // Received positions prop
    symbolStrategyBaseQty,
}) {
    const { users } = useGlobalData();
    // console.log("Pledge Plus Order Comparison Modal", orders);
    // Cancel state
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successModalActionType, setSuccessModalActionType] = useState("Exit");
    const [ordersToCancel, setOrdersToCancel] = useState([]);
    const [submittedOrders, setSubmittedOrders] = useState([]);

    // Selection & Modify State
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [modifyModalOpen, setModifyModalOpen] = useState(false);
    const [ordersToModify, setOrdersToModify] = useState([]);

    // Helper to get multiplier from user object in global data
    function getMultiplier(clientId, asset) {
        if (!users || !clientId || !asset) return 1;
        const user = users.find(u => u.client_id === clientId);
        if (!user) return 1;
        const key = `${asset.toLowerCase()}_multiplier`;
        const val = Number(user[key]);
        return val > 0 ? val : 1;
    }

    // Helper to determine Order Type with Context
    function getOrderType(order, position) {
        const sensitiveStatus = (order.status || "").toLowerCase();

        // If it's traded/complete, it's FILLED
        if (sensitiveStatus === 'complete' || sensitiveStatus === 'traded' || sensitiveStatus === 'filled') {
            return { label: "FILLED", color: "text-blue-400 bg-blue-900/20" };
        }

        // If it's Pending
        const orderSide = String(order.side || "").trim().toLowerCase();
        const isOrderBuy = orderSide === "1" || orderSide === "buy" || orderSide === "b";

        // Check Position Context
        let hasPosition = false;
        let isPositionLong = false;

        if (position) {
            const netQty = parseFloat(position.net_qty || position.qty || 0);
            if (netQty !== 0) {
                hasPosition = true;
                isPositionLong = netQty > 0;
            }
        }

        if (!hasPosition) {
            // No Position -> Any Pending Order is a Potential ENTRY
            return { label: "ENTRY", color: "text-green-400 bg-green-900/20" };
        } else {
            // Position Exists
            // Check if Order is Opposite to Position
            const isOpposite = (isPositionLong && !isOrderBuy) || (!isPositionLong && isOrderBuy);

            if (isOpposite) {
                // Opposite side pending order -> SL (or Exit)
                // We can check trigger price to be more specific if needed, but strictly opposite pending is generic Exit/SL
                return { label: "SL", color: "text-amber-400 bg-amber-900/20" };
            } else {
                // Same side pending order -> Adding to Position (ENTRY)
                return { label: "ENTRY", color: "text-green-400 bg-green-900/20" };
            }
        }
    }

    // Group orders by symbol+strategy for comparison
    const ordersBySymbolStrategy = useMemo(() => {
        if (!orders || !clientData?.clientId || !symbolStrategyBaseQty) return [];

        const rows = [];
        const processedKeys = new Set();

        // Get this client's positions for lookup - handle string/number mismatch & property name variance
        const clientPositions = positions ? positions.filter(p => {
            const pId = p.clientId || p.client_id;
            const match = String(pId).trim() === String(clientData.clientId).trim();

            // DEBUG: Log rejection for Silver to see why it fails
            if (!match && p.symbol.includes("SILVER")) {
                console.log(`[Modal Filter] Rejected Pos: ${p.symbol} | ID: '${pId}' vs Target: '${clientData.clientId}'`);
            }
            return match;
        }) : [];

        // Get ALL orders for this client (Buy AND Sell)
        const clientOrders = orders.filter((o) => {
            const side = String(o.side || "").trim().toLowerCase();
            const cId = o.clientId || o.client_id;
            // Allow Buy (1) and Sell (-1/2)
            const isBuy = side === "1" || side === "buy" || side === "b";
            const isSell = side === "-1" || side === "2" || side === "sell" || side === "s";

            // Strict Status Filter to match PledgePlusOrdersView logic
            // 4 = Traded?, 6 = Pending
            const isValidStatus = String(o.status_no) === "4" || String(o.status_no) === "6";

            return (
                (isBuy || isSell) &&
                cId === clientData.clientId &&
                isValidStatus
            );
        });
        
        if (clientData.clientId === "CA00246") {
            console.log("Showing (filtered) orders for CA00246:", clientOrders);
        }

        clientOrders.forEach((order) => {
            // -- SL EXCLUSION LOGIC START --
            // Check if this order is an SL to exclude it entirely
            // Re-use logic: Pending + Opposite + Position Match

            // 1. Strict Status Filter (Already applied: 4 or 6)
            // 2. Identify Type using helper (we can't call getOrderType easily inside loop without finding position first? 
            //    Actually we find `relatedPos` LATER in the loop. We should move that up or duplicate logic.)

            // Let's find related Position EARLY
            const relatedPosForCheck = clientPositions.find(p => {
                const cleanPSym = (p.symbol || "").toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "").trim();
                const cleanOSym = (order.symbol || "").toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "").trim();
                // Fyers futures still have NSE prefix but must be caught together, their prefixes were removed by regex.
                if (cleanPSym !== cleanOSym) return false;

                const pStrat = (p.strategy || "").toString().trim().toUpperCase();
                const oStrat = (order.strategy || "").toString().trim().toUpperCase();
                const pClean = pStrat === "N/A" ? "" : pStrat;
                const oClean = oStrat === "N/A" ? "" : oStrat;
                return pClean === oClean;
            });

            // Determine if SL
            const sensitiveStatus = (order.status || "").toLowerCase();
            // If Traded/Filled (Status 4), it's NOT an SL (it's filled).
            // Only Pending (Status 6) can be SL.
            const isPendingCheck = String(order.status_no) === "6" || sensitiveStatus === 'pending';

            if (isPendingCheck && relatedPosForCheck) {
                // DEBUG LOG
                console.log(`[SL Check] Checking Order ${order.order_id} (${order.symbol}) against Pos (${relatedPosForCheck.symbol})`);

                const netQty = parseFloat(relatedPosForCheck.net_qty || relatedPosForCheck.qty || 0);
                const isLong = netQty > 0;
                const isShort = netQty < 0; // Should rely on netQty, not just !=0

                const sCheck = String(order.side || "").trim().toLowerCase();
                const isBuyCheck = sCheck === "1" || sCheck === "buy" || sCheck === "b";

                console.log(`[SL Check] PosNetQty: ${netQty}, OrderBuy: ${isBuyCheck}`);

                if ((isLong && !isBuyCheck) || (isShort && isBuyCheck)) {
                    console.log(`[SL Check] MATCHED SL! Skipping.`);
                    // It is an SL order -> SKIP
                    return;
                } else {
                    console.log(`[SL Check] Side Mismatch. Not SL.`);
                }
            } else if (order.symbol.includes("SILVER")) {
                console.log(`[SL Check] NO MATCH for ${order.symbol}. Pending: ${isPendingCheck}, FoundPos: ${!!relatedPosForCheck}`);
                if (!relatedPosForCheck) {
                    // Log why lookups failed
                    clientPositions.forEach(p => console.log("   - Available Pos:", p.symbol, p.strategy, p.client_id));
                }
            }
            // -- SL EXCLUSION LOGIC END --

            const rowUniqueKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}_${order.order_id || order.orderId}`;
            const aggregationKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}_${order.stop_price || 0}_${order.limit_price || 0}`;
            const lookupKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}`;

            if (processedKeys.has(rowUniqueKey)) return;
            processedKeys.add(rowUniqueKey);

            const baseQtyInfo = symbolStrategyBaseQty[lookupKey];

            // If order is not part of our specific "PledgePlus" set (e.g. it's a Nifty order), skip it
            // The hook calculated baseQtyInfo ONLY for PledgePlus assets, so this checks validity automatically
            if (!baseQtyInfo) return;

            const asset = baseQtyInfo.index; // 'index' holds the asset name in our data structure
            const multiplier = getMultiplier(clientData.clientId, asset);
            const baseQty = baseQtyInfo.baseQty;
            const expectedQty = multiplier * baseQty;

            // Calculate Actual Qty as Max(Buy, Sell) to match Base Qty logic
            let sumBuy = 0;
            let sumSell = 0;

            clientOrders.forEach((o) => {
                const oKey = `${o.symbol}_${o.strategy || "NO_STRATEGY"}_${o.stop_price || 0}_${o.limit_price || 0}`;
                if (oKey === aggregationKey) {
                    const q = Math.abs(parseFloat(o.qty || 0));
                    const s = String(o.side || "").trim().toLowerCase();
                    const isBuy = s === "1" || s === "buy" || s === "b";

                    if (isBuy) sumBuy += q;
                    else sumSell += q;
                }
            });

            const actualQty = Math.max(sumBuy, sumSell);

            const sideStr = String(order.side || "").trim().toLowerCase();
            const isBuy = sideStr === "1" || sideStr === "buy" || sideStr === "b";
            const sideLabel = isBuy ? "BUY" : "SELL";

            // Find related position with robust matching
            const relatedPos = clientPositions.find(p => {
                // Normalize symbols by removing exchange prefixes
                const cleanPSym = (p.symbol || "").toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "").trim();
                const cleanOSym = (order.symbol || "").toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "").trim();
                if (cleanPSym !== cleanOSym) return false;

                const pStrat = (p.strategy || "").toString().trim().toUpperCase();
                const oStrat = (order.strategy || "").toString().trim().toUpperCase();

                // Handle "N/A" or empty strategies as equivalent
                const pClean = pStrat === "N/A" ? "" : pStrat;
                const oClean = oStrat === "N/A" ? "" : oStrat;

                return pClean === oClean;
            });

            rows.push({
                symbol: order.symbol,
                side: sideLabel, // Add Side
                strategy: order.strategy || "N/A",
                asset: asset,
                baseQty: baseQty,
                multiplier: multiplier,
                expectedQty: expectedQty,
                actualQty: actualQty,
                stop_price: order.stop_price,
                limit_price: order.limit_price,
                order_id: order.order_id || order.orderId || "N/A",
                orderdatetime: order.orderdatetime || order.orderDateTime || "N/A",
                match: actualQty === expectedQty,
                clientsTrading: baseQtyInfo.clients,
                strike: extractStrike(order.symbol),
                status: order.status, // Pass status for type check
                trigger_price: order.trigger_price, // Pass trigger for type check
                relatedPosition: relatedPos, // Pass position for type logic
                sideVal: order.side // Pass raw side for logic if needed (redundant since we have sideLabel/isBuy but good for safety)
            });
        });

        // DEBUG: Log included Silver orders in Modal
        clientOrders.forEach(o => {
            if (o.symbol.includes("SILVER")) {
                console.log(`[Modal] INCLUDED Order: ${o.order_id} | Status: ${o.status_no} | Side: ${o.side} | Strat: ${o.strategy} | qty: ${o.qty}`);
            }
        });

        if (clientData.clientId === "CA00246") {
            console.log("Final showing rows for CA00246:", rows);
        }

        return rows;
    }, [orders, clientData, symbolStrategyBaseQty, users, positions]); // Added positions dependency

    // -- CANCEL HANDLERS --
    const handleCancelSingle = (orderRow) => {
        // Find the full order object from `orders` prop to pass to modal
        const fullOrder = orders.find(o =>
            String(o.order_id || o.orderId) === String(orderRow.order_id) &&
            (o.clientId || o.client_id) === clientData.clientId
        );

        if (fullOrder) {
            setOrdersToCancel([fullOrder]);
            setConfirmModalOpen(true);
        }
    };

    const handleConfirmCancel = async () => {
        if (!ordersToCancel.length) {
            setConfirmModalOpen(false);
            return;
        }

        try {
            setSuccessModalActionType("Cancel");
            const xtsOrders = [];
            const xtsAnuragOrders = [];
            const godseyeOrders = [];

            // Find broker for client
            let broker = "fyers";
            if (users && clientData?.clientId) {
                const user = users.find(u => String(u.client_id).trim() === String(clientData.clientId).trim());
                if (user && user.broker) broker = user.broker.toLowerCase();
            }

            ordersToCancel.forEach(order => {
                const clientId = order.clientId || order.client_id || clientData.clientId;

                const payloadOrder = {
                    orderid: String(order.order_id || order.orderId),
                    clientid: clientId,
                    broker: broker,
                };

                if (broker === "dhan_xts" || broker === "xts" || broker === "dhan_ss" || broker === "dhan") {
                    xtsOrders.push(payloadOrder);
                } else if (broker === "dhan_xts_anurag" || broker === "dhan_ss_anurag" || broker === "dhan_anurag") {
                    xtsAnuragOrders.push(payloadOrder);
                } else {
                    godseyeOrders.push(payloadOrder);
                }
            });

            const promises = [];
            if (xtsOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.ss_dhan.baseUrl}manual-cancel-orders-xts`, xtsOrders)
                        .then(res => ({ type: 'xts', data: res.data }))
                        .catch(err => ({ type: 'xts', error: err }))
                );
            }
            if (xtsAnuragOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.ss_dhan_anurag.baseUrl}manual-cancel-orders-xts`, xtsAnuragOrders)
                        .then(res => ({ type: 'xts_anurag', data: res.data }))
                        .catch(err => ({ type: 'xts_anurag', error: err }))
                );
            }
            if (godseyeOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.godseye.baseUrl}manual_cancel_orders`, godseyeOrders)
                        .then(res => ({ type: 'godseye', data: res.data }))
                        .catch(err => ({ type: 'godseye', error: err }))
                );
            }

            const results = await Promise.all(promises);
            let allSubmittedOrders = [];
            let errorMsg = "";

            results.forEach(res => {
                if (res.error) {
                    console.error(`❌ Error cancelling ${res.type} orders:`, res.error);
                    errorMsg += `${res.type.toUpperCase()} failed: ${res.error.response?.data?.detail || res.error.message}. `;
                } else if (res.data && res.data.orders) {
                    const responseOrders = res.data.orders.map(o => ({
                        ...o,
                        orderId: o.orderid,
                        symbol: ordersToCancel[0]?.symbol
                    }));
                    allSubmittedOrders = [...allSubmittedOrders, ...responseOrders];
                }
            });

            if (allSubmittedOrders.length > 0) {
                setSubmittedOrders(allSubmittedOrders);
                setSuccessModalOpen(true);
            }

            if (errorMsg) {
                if (allSubmittedOrders.length === 0) {
                    alert(`Failed to cancel orders: ${errorMsg}`);
                } else {
                    console.warn("Partial cancellation error:", errorMsg);
                }
            }

            setConfirmModalOpen(false);

        } catch (error) {
            console.error("❌ Unexpected error cancelling orders:", error);
            alert(`Unexpected error: ${error.message}`);
            setConfirmModalOpen(false);
        }
    };

    const handleRemoveFromBatch = (orderId) => {
        setOrdersToCancel(prev => {
            const newOrders = prev.filter(o => {
                const oid = o.order_id || o.orderId || o.appOrderID;
                return String(oid) !== String(orderId);
            });
            if (newOrders.length === 0) {
                setConfirmModalOpen(false);
            }
            return newOrders;
        });
    };

    // -- FILTER LOGIC --
    const [selectedAsset, setSelectedAsset] = useState("ALL");

    const uniqueAssets = useMemo(() => {
        const assets = new Set(ordersBySymbolStrategy.map(r => r.asset).filter(Boolean));
        return ["ALL", ...Array.from(assets).sort()];
    }, [ordersBySymbolStrategy]);

    const filteredRows = useMemo(() => {
        if (selectedAsset === "ALL") return ordersBySymbolStrategy;
        return ordersBySymbolStrategy.filter(r => r.asset === selectedAsset);
    }, [ordersBySymbolStrategy, selectedAsset]);

    function extractStrike(symbol) {
        const match = symbol?.match(/(\d{3,6})(CE|PE)$/); // adjusted regex for commodities if needed
        return match ? match[1] : "-";
    }

    // Format price with commas and 2 decimals
    function formatPrice(num) {
        if (num === undefined || num === null || num === 0) return "-";
        return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    function formatOrderTime(dt) {
        if (!dt || dt === "N/A") return "-";
        try {
            const date = new Date(dt);
            return date.toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            });
        } catch (e) {
            return dt;
        }
    }

    // Selection Handlers
    const toggleOrderSelection = (orderId) => {
        const next = new Set(selectedOrderIds);
        if (next.has(orderId)) next.delete(orderId);
        else next.add(orderId);
        setSelectedOrderIds(next);
    };

    const handleBulkModify = () => {
        const toMod = filteredRows
            .filter(r => selectedOrderIds.has(r.order_id))
            .map(r => {
                // Find full order object
                const fullOrder = orders.find(o => String(o.order_id || o.orderId) === String(r.order_id));
                return {
                    ...fullOrder,
                    expectedQty: r.expectedQty
                };
            });

        if (toMod.length === 0) {
            toast.error("Select orders to modify");
            return;
        }
        setOrdersToModify(toMod);
        setModifyModalOpen(true);
    };

    const handleSingleModify = (row) => {
        const fullOrder = orders.find(o => String(o.order_id || o.orderId) === String(row.order_id));
        setOrdersToModify([{
            ...fullOrder,
            expectedQty: row.expectedQty
        }]);
        setModifyModalOpen(true);
    };

    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4"
                onClick={onClose}
            >
                <div
                    className="bg-[#1a1d29] rounded-lg shadow-xl max-w-7xl w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-700/50"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-[#14161f] px-6 py-4 border-b border-gray-700 flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <h2 className="text-xl font-bold text-white">Pledge Plus Order Comparison</h2>
                            {selectedOrderIds.size > 0 && (
                                <button
                                    onClick={handleBulkModify}
                                    className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded transition-all shadow-lg flex items-center gap-2"
                                >
                                    Modify Selected ({selectedOrderIds.size})
                                </button>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-white transition-colors text-2xl font-bold leading-none"
                        >
                            ×
                        </button>
                    </div>

                    {/* Client Info */}
                    <div className="bg-[#1a1d29] px-6 py-3 border-b border-gray-700/50">
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-400">Client:</span>
                            <span className="text-white font-semibold">
                                {clientData?.clientName || clientData?.clientId}
                            </span>
                            <span className="text-gray-400">({clientData?.clientId})</span>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-auto">
                        {ordersBySymbolStrategy.length === 0 ? (
                            <div className="text-center py-8 px-6">
                                <p className="text-gray-400 mb-4">
                                    No orders found for comparison
                                </p>
                            </div>
                        ) : (
                            <div className="bg-[#14161f] overflow-hidden">
                                <div className="overflow-auto max-h-[calc(80vh-280px)]">
                                    <table className="w-full text-sm">
                                        <thead className="bg-[#0f1117] sticky top-0 z-10">
                                            <tr className="border-b border-gray-700/30">
                                                <th className="w-8 px-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                                                        checked={filteredRows.length > 0 && filteredRows.every(r => selectedOrderIds.has(r.order_id))}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedOrderIds(new Set(filteredRows.map(r => r.order_id)));
                                                            } else {
                                                                setSelectedOrderIds(new Set());
                                                            }
                                                        }}
                                                    />
                                                </th>
                                                <th className="text-left py-3 px-4 text-gray-400 font-medium">Order ID</th>
                                                <th className="text-left py-3 px-4 text-gray-400 font-medium">Time</th>
                                                <th className="text-left py-3 px-4 text-gray-400 font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <span>Asset</span>
                                                        <select
                                                            value={selectedAsset}
                                                            onChange={(e) => setSelectedAsset(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="bg-[#1a1d29] border border-gray-700 text-gray-300 text-xs rounded px-1 py-0.5 focus:outline-none focus:border-blue-500"
                                                        >
                                                            {uniqueAssets.map(asset => (
                                                                <option key={asset} value={asset}>
                                                                    {asset === "ALL" ? "All" : asset.toUpperCase()}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Side</th>
                                                <th className="text-left py-3 px-4 text-gray-400 font-medium">Symbol</th>
                                                <th className="text-left py-3 px-4 text-gray-400 font-medium">Strategy</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Strike</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">Stop Price</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">Limit Price</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">LTP</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Base (1x)</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Mul</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Exp Qty</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Act Qty</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Match</th>
                                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredRows.map((row, idx) => {
                                                const isMatch = row.actualQty === row.expectedQty;
                                                const qtyColor = isMatch ? "text-green-400" : "text-red-400";
                                                // console.log("ROW--", row);
                                                // Calculate type dynamically
                                                const orderType = getOrderType({
                                                    status: row.status,
                                                    side: row.sideVal || (row.side === "BUY" ? "1" : "-1")
                                                }, row.relatedPosition);

                                                const sensitiveStatus = (row.status || "").toLowerCase();
                                                const isPending = sensitiveStatus === "pending" || sensitiveStatus === "open" || sensitiveStatus === "trigger pending" || sensitiveStatus === "not sent" || String(row.status_no) === "6";


                                                return (
                                                    <tr key={idx} className="border-b border-gray-800/30 hover:bg-[#1a1d29] transition-colors">
                                                        <td className="px-2 text-center">
                                                            <input
                                                                type="checkbox"
                                                                className="w-3.5 h-3.5 rounded border-gray-800 bg-gray-950 text-blue-600 focus:ring-blue-500"
                                                                checked={selectedOrderIds.has(row.order_id)}
                                                                onChange={() => toggleOrderSelection(row.order_id)}
                                                            />
                                                        </td>
                                                        <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">{row.order_id}</td>
                                                        <td className="py-2.5 px-4 text-gray-300 text-xs">{formatOrderTime(row.orderdatetime)}</td>
                                                        <td className="py-2.5 px-4"><span className="text-blue-400 text-xs font-semibold">{row.asset}</span></td>

                                                        <td className="py-2.5 px-4 text-center">
                                                            <span className={`text-xs font-bold ${row.side === "BUY" ? "text-green-400" : "text-red-400"}`}>
                                                                {row.side}
                                                            </span>
                                                        </td>
                                                        <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">{row.symbol}</td>
                                                        <td className="py-2.5 px-4 text-gray-400 text-xs">{row.strategy}</td>
                                                        <td className="py-2.5 px-4 text-center text-gray-300">{row.strike}</td>
                                                        <td className="py-2.5 px-4 text-center text-gray-300">{row.stop_price}</td>
                                                        <td className="py-2.5 px-4 text-center text-gray-300">{row.limit_price}</td>
                                                        <td className="py-2.5 px-4 text-center text-blue-400 font-medium">
                                                            <LiveLTPCell symbol={row.symbol} formatFn={formatPrice} />
                                                        </td>
                                                        <td className="py-2.5 px-4 text-center text-white font-medium">{row.baseQty}</td>
                                                        <td className="py-2.5 px-4 text-center text-purple-400 font-medium">{row.multiplier}x</td>
                                                        <td className="py-2.5 px-4 text-center text-yellow-400 font-medium">{row.expectedQty}</td>
                                                        <td className={`py-2.5 px-4 text-center font-bold ${qtyColor}`}>{row.actualQty}</td>
                                                        <td className="py-2.5 px-4 text-center">
                                                            {isMatch ? <span className="text-green-400">✓</span> : <span className="text-red-400">✗</span>}
                                                        </td>
                                                        <td className="py-2.5 px-4 text-center">
                                                            {isPending ? (
                                                                <div className="flex items-center justify-center gap-1">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleSingleModify(row);
                                                                        }}
                                                                        className="p-1 text-blue-400 hover:text-white hover:bg-blue-600/20 border border-blue-800/50 rounded transition-all"
                                                                        title="Modify Order"
                                                                    >
                                                                        <Pencil className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleCancelSingle(row)}
                                                                        className="px-2 py-1 bg-red-900/40 hover:bg-red-800 text-red-300 hover:text-white rounded text-xs transition-colors border border-red-800/50"
                                                                        title="Cancel Order"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-600">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Cancel Modals */}
            <CancelConfirmationModal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleConfirmCancel}
                orders={ordersToCancel}
                onDelete={handleRemoveFromBatch}
            />

            <ExitSuccessModal
                isOpen={successModalOpen}
                onClose={() => setSuccessModalOpen(false)}
                orders={submittedOrders}
                actionType={successModalActionType}
            />

            <BulkModifyModal
                isOpen={modifyModalOpen}
                onClose={() => setModifyModalOpen(false)}
                orders={ordersToModify}
                onSuccess={() => {
                    setSelectedOrderIds(new Set());
                }}
            />
        </>
    );
}
